import { UploadedDataResponse } from 'src/types';
import { DataSet } from 'src/features/DataUpload/types';
import { attachCurrency } from 'src/utils/currency';

/**
 * Get available metrics for each data type based on the uploaded data
 */
export const getAvailableMetrics = (
  data: UploadedDataResponse['data'] | null,
): Record<string, string[]> => {
  if (!data) {
    return {
      daily: ['spend'],
      weekly: ['spend'],
      monthly: ['spend'],
      offline: ['spend'],
      kpi: [],
    };
  }

  return {
    daily: [
      'spend',
      ...(data.dailyData.some((d) => d.impressions) ? ['impressions'] : []),
    ],
    weekly: [
      'spend',
      ...(data.weeklyData.some((d) => d.impressions) ? ['impressions'] : []),
    ],
    monthly: [
      'spend',
      ...(data.monthlyData.some((d) => d.impressions) ? ['impressions'] : []),
    ],
    offline: [
      'spend',
      ...(data.offlineData.some((d) => d.trps) ? ['trps'] : []),
    ],
    kpi: Array.from(
      new Set(
        data.kpiData.flatMap((d) =>
          Object.entries(d.metrics ?? {})
            .filter(
              ([, value]) =>
                value != null &&
                Number.isFinite(Number(value)) &&
                Number(value) !== 0,
            )
            .map(([metricName]) => metricName),
        ),
      ),
    ).sort(),
  };
};

/**
 * Get available media/sources for each data type
 */
export const getAvailableMediaSources = (
  data: UploadedDataResponse['data'] | null,
): Record<string, string[]> => {
  if (!data) {
    return {
      daily: [],
      weekly: [],
      monthly: [],
      offline: [],
    };
  }

  const extractUniqueMediaSources = (
    dataArray: Array<{ media?: string; source?: string }>,
  ): string[] => {
    const mediaSources = new Set<string>();
    dataArray.forEach((item) => {
      if (item.media && item.media.trim()) {
        mediaSources.add(item.media);
      }
      if (item.source && item.source.trim()) {
        mediaSources.add(item.source);
      }
    });
    return Array.from(mediaSources).sort();
  };

  return {
    daily: extractUniqueMediaSources(data.dailyData),
    weekly: extractUniqueMediaSources(data.weeklyData),
    monthly: extractUniqueMediaSources(data.monthlyData),
    offline: extractUniqueMediaSources(data.offlineData),
  };
};

/**
 * Helper function to get metric value from item
 */
export const getMetricValue = (
  item: Record<string, unknown>,
  metric: string,
): number => {
  switch (metric) {
    case 'spend':
      return parseFloat((item.spend as string) || '0');
    case 'impressions':
      return parseFloat((item.impressions as string) || '0');
    case 'trps':
      return parseFloat((item.trps as string) || '0');
    default: {
      // KPI metrics are dynamic and live in the row's `metrics` object
      const metrics = item.metrics as Record<string, unknown> | undefined;
      const value = metrics?.[metric];
      return value == null ? 0 : parseFloat(String(value)) || 0;
    }
  }
};

/**
 * Helper function to generate chart data
 */
export const generateChartData = (
  chartData: Record<string, unknown>[],
  dateField: string,
  metric: string,
  selectedMediaSource?: string,
) => {
  if (!chartData || chartData.length === 0) {
    return [];
  }

  // Filter data by media/source if selected (not "all")
  const filteredData =
    selectedMediaSource && selectedMediaSource !== 'all'
      ? chartData.filter(
          (item) =>
            item.media === selectedMediaSource ||
            item.source === selectedMediaSource,
        )
      : chartData;

  return filteredData
    .reduce(
      (acc, item) => {
        const date = new Date(item[dateField] as string).toLocaleDateString(
          'en-GB',
        );
        const value = getMetricValue(item, metric);

        // Check if we already have an entry for this date
        const existingIndex = acc.findIndex(
          (d: { date: string; value: number }) => d.date === date,
        );

        if (existingIndex >= 0) {
          // Update existing entry by adding the value
          acc[existingIndex].value += value;
        } else {
          // Add new entry
          acc.push({ date, value });
        }
        return acc;
      },
      [] as Array<{ date: string; value: number }>,
    )
    .sort(
      (a: { date: string }, b: { date: string }) =>
        new Date(a.date.split('/').reverse().join('-')).getTime() -
        new Date(b.date.split('/').reverse().join('-')).getTime(),
    );
};

/**
 * Transform uploaded data into DataSet format for charts
 */
export const transformDataToDataSets = (
  data: UploadedDataResponse['data'] | null,
  selectedMetrics: Record<string, string>,
  selectedMediaSources?: Record<string, string>,
): DataSet[] => {
  if (!data) return [];

  const datasets: DataSet[] = [];

  // Daily Data Chart
  if (data.dailyData.length > 0) {
    const dailyChartData = generateChartData(
      data.dailyData as unknown as Record<string, unknown>[],
      'date',
      selectedMetrics.daily,
      selectedMediaSources?.daily,
    );

    datasets.push({
      id: 'daily-data',
      name: 'Daily Data',
      data: dailyChartData,
      isExpanded: true,
    });
  }

  // Weekly Data Chart
  if (data.weeklyData.length > 0) {
    const weeklyChartData = generateChartData(
      data.weeklyData as unknown as Record<string, unknown>[],
      'weekStartDate',
      selectedMetrics.weekly,
      selectedMediaSources?.weekly,
    );

    datasets.push({
      id: 'weekly-data',
      name: 'Weekly Data',
      data: weeklyChartData,
      isExpanded: false,
    });
  }

  // Monthly Data Chart
  if (data.monthlyData.length > 0) {
    const monthlyChartData = generateChartData(
      data.monthlyData as unknown as Record<string, unknown>[],
      'monthStartDate',
      selectedMetrics.monthly,
      selectedMediaSources?.monthly,
    );

    datasets.push({
      id: 'monthly-data',
      name: 'Monthly Data',
      data: monthlyChartData,
      isExpanded: false,
    });
  }

  // Offline Data Chart
  if (data.offlineData.length > 0) {
    const offlineChartData = generateChartData(
      data.offlineData as unknown as Record<string, unknown>[],
      'periodStartDate',
      selectedMetrics.offline,
      selectedMediaSources?.offline,
    );

    datasets.push({
      id: 'offline-data',
      name: 'Offline Data',
      data: offlineChartData,
      isExpanded: false,
    });
  }

  // KPI Data Chart
  if (data.kpiData.length > 0) {
    const kpiChartData = generateChartData(
      data.kpiData as unknown as Record<string, unknown>[],
      'date',
      selectedMetrics.kpi,
    );

    datasets.push({
      id: 'kpi-data',
      name: 'KPI Data',
      data: kpiChartData,
      isExpanded: false,
    });
  }

  return datasets;
};

/**
 * Format chart data for display
 */
export const formatChartData = (
  chartData: Array<{ date: string; value: number }>,
) => {
  return chartData.map((item) => ({
    date: item.date,
    value: item.value,
  }));
};

/**
 * Format Y-axis values based on data type and metric
 */
export const formatYAxis = (
  value: number,
  dataType: string,
  selectedMetrics: Record<string, string>,
  currencyCode = 'EUR',
): string => {
  if (dataType === 'kpi-data') {
    // The preview has no user-declared unit (that's chosen later on the
    // model form as kpi_unit), so no currency guessing from metric names —
    // all KPI metrics render as plain numbers here
    return value.toLocaleString();
  }

  // Extract the data type prefix (daily, weekly, monthly, offline)
  const dataTypePrefix = dataType?.replace('-data', '');
  const currentMetric = dataTypePrefix ? selectedMetrics[dataTypePrefix] : '';

  // Return numbers only for impressions and TRPs
  if (currentMetric === 'impressions' || currentMetric === 'trps') {
    return value.toLocaleString();
  }

  return attachCurrency(`${(value / 1000).toFixed(2)}K`, currencyCode);
};

/**
 * Calculate total/sum of all values in a dataset
 */
export const calculateTotal = (
  chartData: Array<{ date: string; value: number }>,
): number => {
  return chartData.reduce((sum, item) => sum + item.value, 0);
};

/**
 * Format total value based on data type and metric
 */
export const formatTotal = (
  total: number,
  dataType: string,
  selectedMetrics: Record<string, string>,
  currencyCode = 'EUR',
): string => {
  if (dataType === 'kpi-data') {
    // The preview has no user-declared unit (that's chosen later on the
    // model form as kpi_unit), so no currency guessing from metric names —
    // all KPI metrics render as plain numbers here
    return total.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  // Extract the data type prefix (daily, weekly, monthly, offline)
  const dataTypePrefix = dataType?.replace('-data', '');
  const currentMetric = dataTypePrefix ? selectedMetrics[dataTypePrefix] : '';

  // Return numbers only for impressions and TRPs
  if (currentMetric === 'impressions' || currentMetric === 'trps') {
    return total.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  return attachCurrency(
    total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    currencyCode,
  );
};

/**
 * Get time period (date range) from chart data
 */
export const getTimePeriod = (
  chartData: Array<{ date: string; value: number }>,
): { startDate: string; endDate: string } | null => {
  if (!chartData || chartData.length === 0) {
    return null;
  }

  // Chart data is already sorted by date
  const startDate = chartData[0].date;
  const endDate = chartData[chartData.length - 1].date;

  return { startDate, endDate };
};
